/**
 * useProposals — parent review queue for kid-suggested chores (Fase 3).
 *
 * Wraps the proposal endpoints on /api/chores:
 *   - useProposals()         GET  /api/chores/proposals            (requireAdmin)
 *   - useApproveProposal()   POST /api/chores/proposals/:id/approve (admin-scope)
 *   - useRejectProposal()    POST /api/chores/proposals/:id/reject  (admin-scope)
 *
 * The list query is enabled only for admin sessions (matches ApprovalsRoute's
 * own admin gate). Approve accepts optional parent adjustments (point_value,
 * min_age, proof_required, recurrence); reject carries an optional note.
 *
 * Mutations surface a toast on failure mirroring useChoreActions' approve/reject
 * (the sibling chore-approval flow) rather than the MemberAdminSection re-auth
 * modal — the Approvals tab keeps a single, simple failure UX.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '../../lib/toast';
import type { ChoreTemplateRow } from '../admin/useChoreTemplates';

export const PROPOSALS_KEY = ['chore-proposals'] as const;

interface FetchError extends Error {
  status: number;
}

async function postJson(url: string, body?: unknown): Promise<unknown> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await globalThis.fetch(url, init);
  if (!r.ok) {
    const err = new Error(`fetch_failed:${r.status}`) as FetchError;
    err.status = r.status;
    throw err;
  }
  try {
    return await r.json();
  } catch {
    return undefined;
  }
}

export function useProposals(enabled: boolean) {
  return useQuery<ChoreTemplateRow[]>({
    queryKey: PROPOSALS_KEY,
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await globalThis.fetch('/api/chores/proposals', {
        credentials: 'same-origin',
      });
      if (!r.ok) {
        const err = new Error(`fetch_failed:${r.status}`) as FetchError;
        err.status = r.status;
        throw err;
      }
      return (await r.json()) as ChoreTemplateRow[];
    },
  });
}

export interface ApproveProposalOverrides {
  point_value?: number;
  min_age?: number;
  proof_required?: 'none' | 'optional' | 'required';
}

export function useApproveProposal() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation<unknown, FetchError, { id: string; overrides?: ApproveProposalOverrides }>({
    mutationFn: ({ id, overrides }) =>
      postJson(`/api/chores/proposals/${id}/approve`, overrides ?? {}),
    onError: () => {
      toast({ kind: 'error', body: t('approvals.error') });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROPOSALS_KEY });
      // A freshly-approved proposal becomes a live template.
      void qc.invalidateQueries({ queryKey: ['admin', 'chore-templates'] });
    },
  });
}

export function useRejectProposal() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation<unknown, FetchError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      postJson(`/api/chores/proposals/${id}/reject`, reason ? { reason } : {}),
    onError: () => {
      toast({ kind: 'error', body: t('approvals.error') });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROPOSALS_KEY });
    },
  });
}
